<template>
  <div class="colours-page">
    <!-- ── PAGE HEADER ── -->
    <div class="page-header">
      <h1 class="page-main-title">🎨 Colour Recognition Practice</h1>
      <p class="page-subtitle">Explore {{ colours.length }} colours - learn, play, and test yourself.</p>
    </div>

    <!-- ══════════════════════════════════════════
         SECTION 1 - COLOUR EXPLORER
    ══════════════════════════════════════════ -->
    <section class="section-card">
      <div class="section-header" @click="toggleSection('explorer')" role="button" tabindex="0" @keydown.enter="toggleSection('explorer')" @keydown.space.prevent="toggleSection('explorer')" :aria-expanded="openSections.explorer" :class="{ 'section-header--collapsed': !openSections.explorer }">
        <div class="section-header__text">
          <h2 class="section-title">🖌️ Colour Explorer</h2>
          <p class="section-desc">Browse all colours by category. Click a card to toggle its label, or use the buttons below.</p>
        </div>
        <span class="section-chevron" :class="{ 'section-chevron--open': openSections.explorer }">›</span>
      </div>

      <transition name="section-collapse" @enter="onEnter" @leave="onLeave">
        <div v-if="openSections.explorer" class="section-body">

      <!-- Search + controls row -->
      <div class="explorer-controls">
        <div class="search-wrap">
          <input
            class="colour-search"
            type="text"
            v-model="searchQuery"
            placeholder="Search a colour, e.g. Coral…"
            maxlength="30"
            aria-label="Search colours"
          />
          <button
            class="clear-btn"
            v-if="searchQuery"
            @click="searchQuery = ''"
            aria-label="Clear search"
          >✕</button>
        </div>
        <div class="label-btns">
          <button class="ctrl-btn ctrl-btn--primary" @click="showAllLabels">Show All Labels</button>
          <button class="ctrl-btn ctrl-btn--outline" @click="hideAllLabels">Hide All Labels</button>
        </div>
      </div>

      <!-- Group filter chips -->
      <div class="group-filter" role="group" aria-label="Filter by colour group">
        <button
          v-for="g in GROUPS"
          :key="g"
          class="group-chip"
          :class="{ 'group-chip--active': activeGroup === g }"
          @click="activeGroup = g"
        >{{ g }}</button>
      </div>

      <!-- Results count -->
      <p class="results-count" v-if="searchQuery || activeGroup !== 'All'">
        Showing <strong>{{ filteredColours.length }}</strong> of {{ colours.length }} colours
        <span v-if="activeGroup !== 'All'"> in <em>{{ activeGroup }}</em></span>
        <span v-if="searchQuery"> matching "<em>{{ searchQuery }}</em>"</span>
      </p>

      <!-- Colour card grid -->
      <div class="colour-grid">
        <div
          v-for="item in filteredColours"
          :key="item.name"
          class="colour-card"
          :style="{ backgroundColor: item.hex }"
          @click="item.labelVisible = !item.labelVisible"
          :aria-label="item.labelVisible ? item.name : 'Colour hidden - click to reveal'"
          role="button"
          tabindex="0"
          @keydown.enter="item.labelVisible = !item.labelVisible"
          @keydown.space.prevent="item.labelVisible = !item.labelVisible"
        >
          <div class="colour-card__label" :class="{ 'colour-card__label--hidden': !item.labelVisible }">
            <span class="colour-card__name">{{ item.name }}</span>
            <span class="colour-card__group">{{ item.group }}</span>
          </div>
          <div class="colour-card__hex" v-if="item.labelVisible">{{ item.hex }}</div>
        </div>
      </div>

      <p v-if="filteredColours.length === 0" class="no-results">
        No colours found for "<strong>{{ searchQuery }}</strong>". Try a different name.
      </p>

      <div class="section-note">
        <strong>Tip:</strong> <em>Click any card to toggle its label. Try hiding all labels and recalling each colour name - this is active retrieval practice.</em>
      </div>
        </div><!-- /section-body -->
      </transition>
    </section>

    <!-- ══════════════════════════════════════════
         SECTION 2 - SWATCH PLAYGROUND
    ══════════════════════════════════════════ -->
    <section class="section-card">
      <div class="section-header" @click="toggleSection('swatches')" role="button" tabindex="0" @keydown.enter="toggleSection('swatches')" @keydown.space.prevent="toggleSection('swatches')" :aria-expanded="openSections.swatches" :class="{ 'section-header--collapsed': !openSections.swatches }">
        <div class="section-header__text">
          <h2 class="section-title">🎭 Swatch Playground</h2>
          <p class="section-desc">Click any grey square to reveal its colour. Compare hues side by side to practise visual differentiation.</p>
        </div>
        <span class="section-chevron" :class="{ 'section-chevron--open': openSections.swatches }">›</span>
      </div>

      <transition name="section-collapse" @enter="onEnter" @leave="onLeave">
        <div v-if="openSections.swatches" class="section-body">

      <div class="swatch-controls">
        <button class="ctrl-btn ctrl-btn--outline" @click="resetSwatches">Reset All</button>
        <button class="ctrl-btn ctrl-btn--primary" @click="revealAllSwatches">Reveal All</button>
      </div>

      <div class="swatch-grid">
        <div
          v-for="item in colours"
          :key="'swatch-' + item.name"
          class="swatch"
          :class="{ 'swatch--active': item.active }"
          :style="item.active ? { backgroundColor: item.hex } : {}"
          @click="item.active = !item.active"
          :title="item.active ? item.name : 'Click to reveal'"
          role="button"
          :aria-label="item.active ? ('Colour: ' + item.name + '. Click to hide.') : 'Hidden colour. Click to reveal.'"
          tabindex="0"
          @keydown.enter="item.active = !item.active"
          @keydown.space.prevent="item.active = !item.active"
        >
        </div>
      </div>

      <div class="section-note">
        <strong>Tip:</strong> <em>Reveal swatches one by one and compare similar hues - can you tell Coral from Salmon, or Teal from Cyan?</em>
      </div>
        </div><!-- /section-body -->
      </transition>
    </section>

    <!-- ══════════════════════════════════════════
         SECTION 3 - COLOUR INSPECTOR
    ══════════════════════════════════════════ -->
    <section class="section-card">
      <div class="section-header" @click="toggleSection('inspector')" role="button" tabindex="0" @keydown.enter="toggleSection('inspector')" @keydown.space.prevent="toggleSection('inspector')" :aria-expanded="openSections.inspector" :class="{ 'section-header--collapsed': !openSections.inspector }">
        <div class="section-header__text">
          <h2 class="section-title">🔬 Colour Inspector</h2>
          <p class="section-desc">Type any colour name, hex code, or RGB value for a full breakdown - including tints, shades, and colour properties.</p>
        </div>
        <span class="section-chevron" :class="{ 'section-chevron--open': openSections.inspector }">›</span>
      </div>

      <transition name="section-collapse" @enter="onEnter" @leave="onLeave">
        <div v-if="openSections.inspector" class="section-body">

      <!-- Input -->
      <div class="inspector-input-row">
        <div class="search-wrap">
          <input
            class="colour-search"
            type="text"
            v-model="inspectorInput"
            placeholder="e.g. Periwinkle, #7986cb, rgb(255,100,0)…"
            aria-label="Colour inspector input"
            @input="onInspectorInput"
          />
          <button class="clear-btn" v-if="inspectorInput" @click="inspectorInput = ''; inspectorResolved = null" aria-label="Clear">✕</button>
        </div>
        <!-- Quick-pick chips from COLOURS array -->
        <div class="inspector-quickpicks">
          <button
            v-for="c in inspectorQuickPicks"
            :key="'qp-' + c.name"
            class="qp-chip"
            :style="{ backgroundColor: c.hex }"
            :title="c.name"
            @click="pickInspectorColour(c)"
          ></button>
        </div>
      </div>

      <!-- Inspector panel - shown when a colour is resolved -->
      <transition name="fade">
        <div v-if="inspectorResolved" class="inspector-panel">

          <!-- Row 1: large swatch + identity -->
          <div class="inspector-top">
            <div class="inspector-swatch" :style="{ backgroundColor: inspectorResolved.hex }"></div>
            <div class="inspector-identity">
              <h3 class="inspector-name">{{ inspectorResolved.name }}</h3>
              <div class="inspector-badges">
                <span class="badge badge--hex">{{ inspectorResolved.hex }}</span>
                <span class="badge" :class="'badge--' + inspectorResolved.temp.toLowerCase()">
                  {{ inspectorResolved.temp === 'Warm' ? '🔥' : inspectorResolved.temp === 'Cool' ? '❄️' : '⚖️' }}
                  {{ inspectorResolved.temp }}
                </span>
                <span class="badge badge--group" v-if="inspectorResolved.group">{{ inspectorResolved.group }}</span>
              </div>
              <!-- Colour values -->
              <div class="inspector-values">
                <div class="val-block">
                  <span class="val-label">HEX</span>
                  <code class="val-code" @click="copyValue(inspectorResolved.hex)" title="Click to copy">{{ inspectorResolved.hex }}</code>
                </div>
                <div class="val-block">
                  <span class="val-label">RGB</span>
                  <code class="val-code" @click="copyValue(inspectorResolved.rgbStr)" title="Click to copy">{{ inspectorResolved.rgbStr }}</code>
                </div>
                <div class="val-block">
                  <span class="val-label">HSL</span>
                  <code class="val-code" @click="copyValue(inspectorResolved.hslStr)" title="Click to copy">{{ inspectorResolved.hslStr }}</code>
                </div>
              </div>
              <p class="copy-hint" v-if="copyFeedback">✅ Copied!</p>
              <p class="copy-hint" v-else>Click any value to copy</p>
            </div>
          </div>

          <!-- Row 2: RGB channel bars -->
          <div class="inspector-channels">
            <div class="channel-row">
              <span class="channel-label">R</span>
              <div class="channel-track">
                <div class="channel-fill channel-fill--r" :style="{ width: (inspectorResolved.r / 255 * 100) + '%' }"></div>
              </div>
              <span class="channel-val">{{ inspectorResolved.r }}</span>
            </div>
            <div class="channel-row">
              <span class="channel-label">G</span>
              <div class="channel-track">
                <div class="channel-fill channel-fill--g" :style="{ width: (inspectorResolved.g / 255 * 100) + '%' }"></div>
              </div>
              <span class="channel-val">{{ inspectorResolved.g }}</span>
            </div>
            <div class="channel-row">
              <span class="channel-label">B</span>
              <div class="channel-track">
                <div class="channel-fill channel-fill--b" :style="{ width: (inspectorResolved.b / 255 * 100) + '%' }"></div>
              </div>
              <span class="channel-val">{{ inspectorResolved.b }}</span>
            </div>
          </div>

          <!-- Row 3: Tints & Shades -->
          <div class="inspector-scale">
            <div class="scale-row">
              <span class="scale-label">Tints</span>
              <div class="scale-swatches">
                <div
                  v-for="(t, i) in inspectorResolved.tints"
                  :key="'tint-' + i"
                  class="scale-swatch"
                  :style="{ backgroundColor: t.hex }"
                  :title="t.hex"
                  @click="copyValue(t.hex)"
                ><span class="scale-swatch-hex">{{ t.hex }}</span></div>
              </div>
            </div>
            <div class="scale-row scale-row--base">
              <span class="scale-label">Base</span>
              <div class="scale-swatches">
                <div
                  class="scale-swatch scale-swatch--base"
                  :style="{ backgroundColor: inspectorResolved.hex }"
                  :title="inspectorResolved.hex"
                  @click="copyValue(inspectorResolved.hex)"
                ><span class="scale-swatch-hex">{{ inspectorResolved.hex }}</span></div>
              </div>
            </div>
            <div class="scale-row">
              <span class="scale-label">Shades</span>
              <div class="scale-swatches">
                <div
                  v-for="(s, i) in inspectorResolved.shades"
                  :key="'shade-' + i"
                  class="scale-swatch"
                  :style="{ backgroundColor: s.hex }"
                  :title="s.hex"
                  @click="copyValue(s.hex)"
                ><span class="scale-swatch-hex">{{ s.hex }}</span></div>
              </div>
            </div>
          </div>

          <!-- Row 4: Fun fact -->
          <div class="inspector-fact" v-if="inspectorResolved.fact">
            <span class="fact-icon">💡</span>
            <p class="fact-text">{{ inspectorResolved.fact }}</p>
          </div>

        </div>
      </transition>

      <!-- Placeholder when nothing typed yet -->
      <div v-if="!inspectorResolved && !inspectorInput" class="inspector-placeholder">
        <span class="placeholder-icon">🎨</span>
        <p>Type a colour above to inspect it.</p>
      </div>
      <!-- No match state -->
      <div v-if="!inspectorResolved && inspectorInput" class="inspector-placeholder">
        <span class="placeholder-icon">🤔</span>
        <p>Could not resolve "<strong>{{ inspectorInput }}</strong>" - try a colour name, hex code (#rrggbb), or rgb(r,g,b).</p>
      </div>

      <div class="section-note">
        <strong>Tip:</strong> <em>Click any hex, RGB, or HSL value to copy it to your clipboard. Click tint/shade swatches to copy those hex codes too.</em>
      </div>
        </div><!-- /section-body -->
      </transition>
    </section>

    <!-- ══════════════════════════════════════════
         SECTION 4 - COLOUR QUIZ
    ══════════════════════════════════════════ -->
    <section class="section-card quiz-section">
      <div class="section-header" @click="toggleSection('quiz')" role="button" tabindex="0" @keydown.enter="toggleSection('quiz')" @keydown.space.prevent="toggleSection('quiz')" :aria-expanded="openSections.quiz" :class="{ 'section-header--collapsed': !openSections.quiz }">
        <div class="section-header__text">
          <h2 class="section-title">🧠 Colour Quiz</h2>
          <p class="section-desc">Can you name the colour shown? {{ totalQuestions }} questions, drawn from all {{ COLOURS.length }} colours.</p>
        </div>
        <span class="section-chevron" :class="{ 'section-chevron--open': openSections.quiz }">›</span>
      </div>

      <transition name="section-collapse" @enter="onEnter" @leave="onLeave">
        <div v-if="openSections.quiz" class="section-body">

      <!-- ── Idle / Start screen ── -->
      <div v-if="quizState === 'idle'" class="quiz-start">
        <div class="quiz-group-preview">
          <div
            v-for="g in GROUPS.filter(g => g !== 'All')"
            :key="'qprev-' + g"
            class="quiz-group-row"
          >
            <span class="quiz-group-label">{{ g }}</span>
            <div class="quiz-group-dots">
              <div
                v-for="c in coloursByGroup(g).slice(0, 6)"
                :key="'dot-' + c.name"
                class="quiz-preview-dot"
                :style="{ backgroundColor: c.hex }"
                :title="c.name"
              ></div>
            </div>
          </div>
        </div>
        <button class="quiz-start-btn" @click="startQuiz">Start Quiz</button>
      </div>

      <!-- ── Playing ── -->
      <div v-if="quizState === 'playing'" class="quiz-play">
        <div class="quiz-progress-wrap">
          <div class="quiz-progress-bar" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="quiz-meta">
          <span class="quiz-counter">Question {{ currentQuestion + 1 }} of {{ totalQuestions }}</span>
          <span class="quiz-score-live">Score: <strong>{{ score }}</strong></span>
        </div>

        <div class="quiz-swatch-wrap">
          <div class="quiz-swatch" :style="{ backgroundColor: currentColor.hex }"></div>
        </div>

        <div class="quiz-options">
          <button
            v-for="option in currentOptions"
            :key="option"
            class="quiz-option-btn"
            :class="getOptionClass(option)"
            :disabled="answered"
            @click="checkAnswer(option)"
          >{{ option }}</button>
        </div>

        <transition name="fade">
          <div v-if="answered" class="quiz-feedback" :class="isCorrect ? 'feedback-correct' : 'feedback-wrong'">
            <div class="feedback-swatch" :style="{ backgroundColor: currentColor.hex }"></div>
            <div class="feedback-text">
              <span v-if="isCorrect">✅ Correct! That's <strong>{{ currentColor.name }}</strong>.</span>
              <span v-else>❌ That's <strong>{{ currentColor.name }}</strong> ({{ currentColor.hex }}).</span>
            </div>
            <button class="ctrl-btn ctrl-btn--primary quiz-next-btn" @click="nextQuestion">
              {{ currentQuestion + 1 < totalQuestions ? 'Next →' : 'See Results' }}
            </button>
          </div>
        </transition>
      </div>

      <!-- ── Results ── -->
      <div v-if="quizState === 'results'" class="quiz-results">
        <div class="quiz-trophy" aria-hidden="true">
          <span v-if="score === totalQuestions">🏆</span>
          <span v-else-if="score >= totalQuestions * 0.75">🥈</span>
          <span v-else-if="score >= totalQuestions * 0.5">🎯</span>
          <span v-else>📚</span>
        </div>
        <h3 class="quiz-results-heading">Quiz Complete!</h3>
        <p class="quiz-final-score">You scored <strong>{{ score }}</strong> out of <strong>{{ totalQuestions }}</strong></p>
        <p class="quiz-result-msg">{{ resultMessage }}</p>

        <div class="quiz-result-breakdown">
          <div
            v-for="(item, i) in quizHistory"
            :key="i"
            class="quiz-history-item"
            :class="item.correct ? 'history-correct' : 'history-wrong'"
          >
            <div class="history-swatch" :style="{ backgroundColor: item.hex }"></div>
            <span class="history-name">{{ item.name }}</span>
            <span class="history-icon">{{ item.correct ? '✅' : '❌' }}</span>
          </div>
        </div>

        <button class="quiz-start-btn" @click="startQuiz">Play Again</button>
      </div>
        </div><!-- /section-body -->
      </transition>
    </section>
  </div>
</template>

<script>
import { COLOURS, GROUPS, FUN_FACTS } from "./colours/coloursData.js";

export default {
  data() {
    return {
      // ── Explorer ──
      colours: COLOURS.map(c => ({ ...c, labelVisible: true, active: false })),
      searchQuery:  "",
      activeGroup:  "All",
      showLabels:   true,

      // ── Section collapse state (all open by default) ──
      openSections: {
        explorer:  true,
        swatches:  true,
        inspector: true,
        quiz:      true,
      },

      // ── Inspector ──
      inspectorInput:    "",
      inspectorResolved: null,
      copyFeedback:      false,

      // ── Quiz ──
      quizColours:     COLOURS,
      totalQuestions:  50,
      quizState:       "idle",   // 'idle' | 'playing' | 'results'
      currentQuestion: 0,
      score:           0,
      currentColor:    null,
      currentOptions:  [],
      answered:        false,
      isCorrect:       false,
      selectedAnswer:  null,
      quizHistory:     [],
      quizQueue:       [],
    };
  },

  computed: {
    COLOURS() { return COLOURS; },
    GROUPS()  { return GROUPS;  },

    filteredColours() {
      let list = this.colours;
      if (this.activeGroup !== "All") {
        list = list.filter(c => c.group === this.activeGroup);
      }
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter(c => c.name.toLowerCase().includes(q));
      }
      return list;
    },

    inspectorQuickPicks() {
      // A curated spread of colours across groups for the quick-pick row
      const picks = ["Red","Orange","Yellow","Green","Teal","Blue","Indigo","Purple","Pink","Coral","Gold","Mint","Lavender","Charcoal","Salmon"];
      return COLOURS.filter(c => picks.includes(c.name));
    },

    progressPercent() {
      return (this.currentQuestion / this.totalQuestions) * 100;
    },

    resultMessage() {
      const pct = this.score / this.totalQuestions;
      if (pct === 1)   return "Perfect score - flawless colour recognition! 🌟";
      if (pct >= 0.8)  return "Excellent! You know your colours really well. 🎨";
      if (pct >= 0.6)  return "Great work - solid colour recognition.";
      if (pct >= 0.4)  return "Good effort - keep practising to sharpen your recall.";
      return "Keep going - every practice session improves your accuracy.";
    },
  },

  methods: {
    // ── Explorer helpers ──
    showAllLabels() {
      this.colours.forEach(c => { c.labelVisible = true; });
    },
    hideAllLabels() {
      this.colours.forEach(c => { c.labelVisible = false; });
    },
    coloursByGroup(group) {
      return COLOURS.filter(c => c.group === group);
    },

    toggleSection(key) {
      this.openSections[key] = !this.openSections[key];
    },

    // JS transition hooks for smooth height animation
    onEnter(el) {
      el.style.height     = "0";
      el.style.opacity    = "0";
      el.style.overflow   = "hidden";
      requestAnimationFrame(() => {
        el.style.transition = "height 0.3s ease, opacity 0.25s ease";
        el.style.height     = el.scrollHeight + "px";
        el.style.opacity    = "1";
      });
    },
    onLeave(el) {
      el.style.height     = el.scrollHeight + "px";
      el.style.overflow   = "hidden";
      requestAnimationFrame(() => {
        el.style.transition = "height 0.3s ease, opacity 0.25s ease";
        el.style.height     = "0";
        el.style.opacity    = "0";
      });
    },

    // ── Inspector helpers ──
    onInspectorInput() {
      const raw = this.inspectorInput.trim();
      if (!raw) { this.inspectorResolved = null; return; }
      this.inspectorResolved = this.resolveColour(raw);
    },
    pickInspectorColour(c) {
      this.inspectorInput = c.name;
      this.inspectorResolved = this.resolveColour(c.name);
    },

    resolveColour(input) {
      const str = input.trim();
      let r, g, b, resolvedHex, resolvedName;

      // 1. Try matching our COLOURS array by name
      const namedMatch = COLOURS.find(c => c.name.toLowerCase() === str.toLowerCase());
      if (namedMatch) {
        resolvedHex  = namedMatch.hex;
        resolvedName = namedMatch.name;
        [r, g, b]    = this.hexToRgb(namedMatch.hex);
      }

      // 2. Try hex (#rgb or #rrggbb)
      if (!resolvedHex) {
        const hexMatch = str.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hexMatch) {
          let h = hexMatch[1];
          if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
          resolvedHex  = '#' + h.toLowerCase();
          resolvedName = resolvedHex;
          [r, g, b]    = this.hexToRgb(resolvedHex);
        }
      }

      // 3. Try rgb(r,g,b)
      if (!resolvedHex) {
        const rgbMatch = str.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
        if (rgbMatch) {
          r = Math.min(255, parseInt(rgbMatch[1]));
          g = Math.min(255, parseInt(rgbMatch[2]));
          b = Math.min(255, parseInt(rgbMatch[3]));
          resolvedHex  = this.rgbToHex(r, g, b);
          resolvedName = resolvedHex;
        }
      }

      // 4. Try as a raw CSS colour name via canvas
      if (!resolvedHex) {
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillStyle = str;
        const testHex = ctx.fillStyle;
        if (testHex !== '#ffffff' || str.toLowerCase() === 'white') {
          resolvedHex  = testHex;
          resolvedName = str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          [r, g, b]    = this.hexToRgb(resolvedHex);
        }
      }

      if (!resolvedHex || r === undefined) return null;

      const [h, s, l]  = this.rgbToHsl(r, g, b);
      const groupMatch = namedMatch ? namedMatch.group : null;
      const temp       = this.getTemperature(h, s, l);
      const tints      = this.generateTints(r, g, b, 7);
      const shades     = this.generateShades(r, g, b, 7);

      return {
        name:    resolvedName,
        hex:     resolvedHex,
        r, g, b,
        rgbStr:  `rgb(${r}, ${g}, ${b})`,
        hslStr:  `hsl(${h}°, ${s}%, ${l}%)`,
        temp,
        group:   groupMatch,
        tints,
        shades,
        fact:    FUN_FACTS[resolvedName] || null,
      };
    },

    hexToRgb(hex) {
      const h = hex.replace('#', '');
      const n = parseInt(h, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    },
    rgbToHex(r, g, b) {
      return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
    },
    rgbToHsl(r, g, b) {
      r /= 255; g /= 255; b /= 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s;
      const l = Math.round(((max + min) / 2) * 100);
      if (max === min) { h = s = 0; }
      else {
        const d = max - min;
        s = Math.round((l > 50 ? d / (2 - max - min) : d / (max + min)) * 100);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          default: h = ((r - g) / d + 4) / 6;
        }
        h = Math.round(h * 360);
      }
      return [h, s, l];
    },
    getTemperature(h, s, l) {
      if (s < 12 || l > 90 || l < 10) return 'Neutral';
      if ((h >= 0 && h < 60) || (h >= 300 && h <= 360)) return 'Warm';
      if (h >= 60 && h < 75) return 'Warm';   // yellow is warm
      return 'Cool';
    },
    generateTints(r, g, b, steps) {
      return Array.from({ length: steps }, (_, i) => {
        const t = (i + 1) / (steps + 1);
        const nr = Math.round(r + (255 - r) * t);
        const ng = Math.round(g + (255 - g) * t);
        const nb = Math.round(b + (255 - b) * t);
        return { hex: this.rgbToHex(nr, ng, nb) };
      }).reverse();
    },
    generateShades(r, g, b, steps) {
      return Array.from({ length: steps }, (_, i) => {
        const t = (i + 1) / (steps + 1);
        const nr = Math.round(r * (1 - t));
        const ng = Math.round(g * (1 - t));
        const nb = Math.round(b * (1 - t));
        return { hex: this.rgbToHex(nr, ng, nb) };
      });
    },

    copyValue(val) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(val).then(() => {
          this.copyFeedback = true;
          setTimeout(() => { this.copyFeedback = false; }, 1800);
        });
      }
    },
    resetSwatches()  { this.colours.forEach(c => { c.active = false; }); },
    revealAllSwatches() { this.colours.forEach(c => { c.active = true; }); },

    // ── Quiz helpers ──
    shuffle(arr) {
      return [...arr].sort(() => Math.random() - 0.5);
    },
    startQuiz() {
      this.score           = 0;
      this.currentQuestion = 0;
      this.quizHistory     = [];
      this.answered        = false;
      this.selectedAnswer  = null;
      this.quizQueue       = this.shuffle(this.quizColours).slice(0, this.totalQuestions);
      this.quizState       = "playing";
      this.loadQuestion();
    },
    loadQuestion() {
      this.answered       = false;
      this.selectedAnswer = null;
      this.currentColor   = this.quizQueue[this.currentQuestion];
      const others        = this.quizColours.filter(c => c.name !== this.currentColor.name);
      const wrongs        = this.shuffle(others).slice(0, 3).map(c => c.name);
      this.currentOptions = this.shuffle([this.currentColor.name, ...wrongs]);
    },
    checkAnswer(option) {
      this.answered      = true;
      this.selectedAnswer = option;
      this.isCorrect     = option === this.currentColor.name;
      if (this.isCorrect) this.score++;
      this.quizHistory.push({
        name:    this.currentColor.name,
        hex:     this.currentColor.hex,
        correct: this.isCorrect,
      });
    },
    nextQuestion() {
      if (this.currentQuestion + 1 < this.totalQuestions) {
        this.currentQuestion++;
        this.loadQuestion();
      } else {
        this.quizState = "results";
      }
    },
    getOptionClass(option) {
      if (!this.answered) return "";
      if (option === this.currentColor.name) return "option-correct";
      if (option === this.selectedAnswer)    return "option-wrong";
      return "";
    },
  },
};
</script>

<style scoped>
/* ── Page shell ─────────────────────────────────────────────── */
.colours-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem 1.25rem 3rem;
  font-family: var(--font-body, "Nunito", sans-serif);
}

/* ── Page header ────────────────────────────────────────────── */
.page-header {
  text-align: center;
  padding: 2rem 1rem 1.5rem;
  background: var(--color-surface, rgba(255,255,255,0.94));
  border: 1px solid var(--color-border, rgba(13,148,136,0.15));
  border-radius: var(--radius-lg, 16px);
  box-shadow: var(--shadow-md, 0 4px 20px rgba(15,23,42,0.1));
  margin-bottom: 1.5rem;
}

.page-main-title {
  font-family: var(--font-brand, "Fredoka", sans-serif);
  font-size: 2.25rem;
  color: var(--color-primary-dark, #0f766e);
  margin: 0 0 0.4rem;
}

.page-subtitle {
  font-size: 1.05rem;
  color: var(--color-text-muted, #64748b);
  margin: 0;
}

/* ── Section card ───────────────────────────────────────────── */
.section-card {
  background: var(--color-surface, rgba(255,255,255,0.94));
  border: 1px solid var(--color-border, rgba(13,148,136,0.15));
  border-radius: var(--radius-lg, 16px);
  box-shadow: var(--shadow-md, 0 4px 20px rgba(15,23,42,0.1));
  padding: 1.75rem 1.75rem 1.5rem;
  margin-bottom: 1.75rem;
}

.section-title {
  font-family: var(--font-brand, "Fredoka", sans-serif);
  font-size: 1.6rem;
  color: var(--color-primary-dark, #0f766e);
  margin: 0 0 0.3rem;
}

.section-desc {
  font-size: 1rem;
  color: var(--color-text-muted, #64748b);
  margin: 0;
}

/* ── Collapsible section header ─────────────────────────────── */
.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  padding-bottom: 1.25rem;
  user-select: none;
  border-radius: var(--radius-sm, 8px);
  transition: background 0.15s ease;
}

.section-header:hover {
  background: var(--color-primary-light, #ccfbf1);
  padding-left: 0.4rem;
  padding-right: 0.4rem;
}

.section-header:focus-visible {
  outline: 3px solid var(--color-primary, #0d9488);
  outline-offset: 2px;
}

/* Collapsed state: remove bottom padding so header sits flush */
.section-header--collapsed {
  padding-bottom: 0;
}

.section-header__text {
  flex: 1;
}

.section-chevron {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--color-primary, #0d9488);
  line-height: 1;
  flex-shrink: 0;
  margin-top: 0.35rem;
  display: inline-block;
  transform: rotate(90deg);
  transition: transform 0.28s ease;
}

.section-chevron--open {
  transform: rotate(-90deg);
}

/* Section body — overflow clipped during animation */
.section-body {
  overflow: hidden;
  transition: height 0.3s ease, opacity 0.25s ease;
}

/* Fade transition used by the inner inspector panel */
.section-note {
  margin-top: 1.25rem;
  font-size: 0.95rem;
  color: var(--color-text-muted, #64748b);
  background: var(--color-primary-light, #ccfbf1);
  border-left: 4px solid var(--color-primary, #0d9488);
  border-radius: 0 var(--radius-sm, 8px) var(--radius-sm, 8px) 0;
  padding: 0.65rem 1rem;
}

/* ── Shared button styles ───────────────────────────────────── */
.ctrl-btn {
  padding: 0.5rem 1.1rem;
  border-radius: var(--radius-sm, 8px);
  font-family: var(--font-body, "Nunito", sans-serif);
  font-weight: 700;
  font-size: 0.92rem;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, border-color 0.2s;
  border: 2px solid transparent;
  white-space: nowrap;
}

.ctrl-btn--primary {
  background: var(--color-primary, #0d9488);
  color: #fff;
  border-color: var(--color-primary, #0d9488);
}
.ctrl-btn--primary:hover {
  background: var(--color-primary-dark, #0f766e);
  border-color: var(--color-primary-dark, #0f766e);
  transform: translateY(-1px);
}

.ctrl-btn--outline {
  background: transparent;
  color: var(--color-primary-dark, #0f766e);
  border-color: var(--color-primary, #0d9488);
}
.ctrl-btn--outline:hover {
  background: var(--color-primary-light, #ccfbf1);
  transform: translateY(-1px);
}

/* ── SECTION 1 - Explorer ───────────────────────────────────── */
.explorer-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
}

.search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 380px;
}

.colour-search {
  width: 100%;
  padding: 0.6rem 2.2rem 0.6rem 1rem;
  border: 2px solid #ccc;
  border-radius: 30px;
  font: inherit;
  font-size: 0.97rem;
  outline: none;
  transition: border-color 0.2s;
}
.colour-search:focus {
  border-color: var(--color-primary, #0d9488);
  box-shadow: 0 0 0 3px rgba(13,148,136,0.15);
}

.clear-btn {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  color: #888;
  padding: 0.2rem 0.4rem;
  border-radius: 50%;
  line-height: 1;
}
.clear-btn:hover { color: #333; background: #f0f0f0; }

.label-btns {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Group filter chips */
.group-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 1rem;
}

.group-chip {
  padding: 0.35rem 0.85rem;
  border-radius: 20px;
  font-size: 0.88rem;
  font-weight: 700;
  font-family: var(--font-body, "Nunito", sans-serif);
  border: 2px solid #ddd;
  background: #f5f5f5;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s, color 0.18s;
  white-space: nowrap;
}
.group-chip:hover {
  background: var(--color-primary-light, #ccfbf1);
  border-color: var(--color-primary, #0d9488);
}
.group-chip--active {
  background: var(--color-primary, #0d9488);
  border-color: var(--color-primary, #0d9488);
  color: #fff;
}

.results-count {
  font-size: 0.9rem;
  color: var(--color-text-muted, #64748b);
  margin: 0 0 0.75rem;
}

/* Colour card grid */
.colour-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.85rem;
}

.colour-card {
  border-radius: var(--radius-md, 12px);
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0.65rem 0.7rem 0.6rem;
  cursor: pointer;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(15,23,42,0.08));
  transition: transform 0.18s, box-shadow 0.18s;
  border: 2px solid rgba(255,255,255,0.3);
  position: relative;
  user-select: none;
}
.colour-card:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: var(--shadow-lg, 0 8px 32px rgba(15,23,42,0.12));
}
.colour-card:focus-visible {
  outline: 3px solid var(--color-accent, #f59e0b);
  outline-offset: 2px;
}

.colour-card__label {
  background: rgba(255,255,255,0.85);
  border-radius: var(--radius-sm, 8px);
  padding: 0.3rem 0.5rem 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 1px;
  transition: opacity 0.2s;
  backdrop-filter: blur(4px);
}
.colour-card__label--hidden {
  opacity: 0;
  pointer-events: none;
}

.colour-card__name {
  font-weight: 700;
  font-size: 0.85rem;
  color: #1e293b;
  line-height: 1.2;
}

.colour-card__group {
  font-size: 0.72rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.colour-card__hex {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 0.65rem;
  background: rgba(0,0,0,0.35);
  color: #fff;
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  font-family: monospace;
  letter-spacing: 0.04em;
}

.no-results {
  text-align: center;
  color: var(--color-text-muted, #64748b);
  font-size: 1rem;
  padding: 2rem 0;
}

/* ── SECTION 2 - Swatch Playground ─────────────────────────── */
.swatch-controls {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 1.1rem;
  flex-wrap: wrap;
}

.swatch-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.6rem;
}

.swatch {
  height: 72px;
  background-color: #bdbdbd;
  border-radius: var(--radius-sm, 8px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.25s, transform 0.18s, box-shadow 0.18s;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(15,23,42,0.08));
  border: 2px solid rgba(255,255,255,0.4);
  user-select: none;
}
.swatch:hover {
  transform: translateY(-2px) scale(1.04);
  box-shadow: var(--shadow-md, 0 4px 20px rgba(15,23,42,0.1));
}
.swatch:focus-visible {
  outline: 3px solid var(--color-accent, #f59e0b);
  outline-offset: 2px;
}

.swatch__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-align: center;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0,0,0,0.55);
  padding: 0 4px;
  line-height: 1.2;
  pointer-events: none;
}

/* ── SECTION 3 - Colour Inspector ───────────────────────────── */
.inspector-input-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: center;
  margin-bottom: 1.25rem;
}

.inspector-quickpicks {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.qp-chip {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.6);
  cursor: pointer;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(15,23,42,0.15));
  transition: transform 0.15s, box-shadow 0.15s;
  flex-shrink: 0;
}
.qp-chip:hover {
  transform: scale(1.2);
  box-shadow: var(--shadow-md, 0 4px 20px rgba(15,23,42,0.18));
}

/* Inspector panel */
.inspector-panel {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Top row: swatch + identity */
.inspector-top {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.inspector-swatch {
  width: 140px;
  height: 140px;
  border-radius: var(--radius-md, 12px);
  border: 3px solid rgba(0,0,0,0.1);
  box-shadow: var(--shadow-lg, 0 8px 32px rgba(15,23,42,0.15));
  flex-shrink: 0;
}

.inspector-identity {
  flex: 1;
  min-width: 200px;
}

.inspector-name {
  font-family: var(--font-brand, "Fredoka", sans-serif);
  font-size: 1.75rem;
  color: var(--color-text, #1e293b);
  margin: 0 0 0.6rem;
}

.inspector-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 0.85rem;
}

.badge {
  padding: 0.25rem 0.7rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.badge--hex      { background: #1e293b; color: #fff; font-family: monospace; }
.badge--warm     { background: #fff3e0; color: #e65100; border: 1px solid #ffcc80; }
.badge--cool     { background: #e3f2fd; color: #1565c0; border: 1px solid #90caf9; }
.badge--neutral  { background: #f5f5f5; color: #455a64; border: 1px solid #cfd8dc; }
.badge--group    { background: var(--color-primary-light, #ccfbf1); color: var(--color-primary-dark, #0f766e); border: 1px solid var(--color-primary, #0d9488); }

.inspector-values {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.val-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.val-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-muted, #64748b);
}

.val-code {
  font-family: monospace;
  font-size: 0.88rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.25rem 0.55rem;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s;
  white-space: nowrap;
}
.val-code:hover {
  background: var(--color-primary-light, #ccfbf1);
  border-color: var(--color-primary, #0d9488);
}

.copy-hint {
  font-size: 0.8rem;
  color: var(--color-text-muted, #64748b);
  margin: 0.5rem 0 0;
  font-style: italic;
}

/* RGB channel bars */
.inspector-channels {
  background: #f8fafc;
  border-radius: var(--radius-sm, 8px);
  padding: 0.85rem 1rem;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.channel-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.channel-label {
  width: 14px;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--color-text, #1e293b);
  flex-shrink: 0;
}

.channel-track {
  flex: 1;
  height: 12px;
  background: #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.channel-fill {
  height: 100%;
  border-radius: 8px;
  transition: width 0.4s ease;
}
.channel-fill--r { background: #ef5350; }
.channel-fill--g { background: #66bb6a; }
.channel-fill--b { background: #42a5f5; }

.channel-val {
  width: 30px;
  text-align: right;
  font-size: 0.82rem;
  font-family: monospace;
  color: var(--color-text-muted, #64748b);
  flex-shrink: 0;
}

/* Tints & Shades scale */
.inspector-scale {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.scale-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.scale-label {
  width: 52px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--color-text-muted, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.scale-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.scale-swatch {
  width: 48px;
  height: 36px;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  border: 1px solid rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.15s, box-shadow 0.15s;
}
.scale-swatch:hover {
  transform: scale(1.12);
  box-shadow: var(--shadow-md, 0 4px 20px rgba(15,23,42,0.18));
  z-index: 1;
}
.scale-swatch:hover .scale-swatch-hex {
  opacity: 1;
}
.scale-swatch--base {
  width: 60px;
  height: 44px;
  border: 2px solid rgba(0,0,0,0.2);
}

.scale-swatch-hex {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  font-family: monospace;
  color: rgba(255,255,255,0.9);
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
  background: rgba(0,0,0,0.25);
  opacity: 0;
  transition: opacity 0.18s;
  padding: 2px;
  text-align: center;
  word-break: break-all;
}

/* Fun fact */
.inspector-fact {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  background: var(--color-accent-light, #fef3c7);
  border: 1px solid #fde68a;
  border-radius: var(--radius-sm, 8px);
  padding: 0.85rem 1rem;
}

.fact-icon {
  font-size: 1.3rem;
  flex-shrink: 0;
  line-height: 1.4;
}

.fact-text {
  margin: 0;
  font-size: 0.95rem;
  color: #78350f;
  line-height: 1.6;
}

/* Placeholder states */
.inspector-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: var(--color-text-muted, #64748b);
  text-align: center;
}
.placeholder-icon { font-size: 2.5rem; }
.inspector-placeholder p { margin: 0; font-size: 1rem; }

/* ── SECTION 4 - Quiz ───────────────────────────────────────── */
.quiz-section { text-align: center; }

/* Start screen */
.quiz-start {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.quiz-group-preview {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.7rem;
  width: 100%;
  max-width: 700px;
  text-align: left;
}

.quiz-group-row {
  background: #f8fafc;
  border-radius: var(--radius-sm, 8px);
  padding: 0.6rem 0.8rem;
  border: 1px solid #e2e8f0;
}

.quiz-group-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-text-muted, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.45rem;
}

.quiz-group-dots {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.quiz-preview-dot {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid rgba(0,0,0,0.12);
  box-shadow: 0 1px 4px rgba(0,0,0,0.18);
  flex-shrink: 0;
}

.quiz-start-btn {
  font-family: var(--font-brand, "Fredoka", sans-serif);
  font-size: 1.15rem;
  font-weight: 700;
  padding: 0.75rem 2.5rem;
  border-radius: 30px;
  background: var(--color-primary, #0d9488);
  color: #fff;
  border: none;
  cursor: pointer;
  box-shadow: var(--shadow-md, 0 4px 20px rgba(15,23,42,0.1));
  transition: background 0.2s, transform 0.15s;
}
.quiz-start-btn:hover {
  background: var(--color-primary-dark, #0f766e);
  transform: translateY(-2px);
}

/* Progress bar */
.quiz-progress-wrap {
  width: 100%;
  max-width: 520px;
  height: 10px;
  background: #e0e0e0;
  border-radius: 10px;
  margin: 0 auto 14px;
  overflow: hidden;
}
.quiz-progress-bar {
  height: 100%;
  background: var(--color-primary, #0d9488);
  border-radius: 10px;
  transition: width 0.4s ease;
}

/* Playing */
.quiz-play {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.quiz-meta {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
  font-size: 1rem;
}
.quiz-counter { color: var(--color-text-muted, #64748b); }
.quiz-score-live { font-weight: 600; }

.quiz-swatch-wrap { margin-bottom: 1.5rem; }
.quiz-swatch {
  width: 180px;
  height: 180px;
  border-radius: var(--radius-md, 12px);
  border: 3px solid rgba(0,0,0,0.12);
  box-shadow: var(--shadow-lg, 0 8px 32px rgba(15,23,42,0.12));
  display: inline-block;
}

.quiz-options {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.85rem;
  margin-bottom: 1.25rem;
}

.quiz-option-btn {
  min-width: 140px;
  padding: 0.75rem 1.4rem;
  font-family: var(--font-body, "Nunito", sans-serif);
  font-size: 1rem;
  font-weight: 700;
  border: 2px solid #bbb;
  border-radius: var(--radius-sm, 8px);
  background: #fff;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s, transform 0.12s;
}
.quiz-option-btn:not(:disabled):hover {
  background: #f0fdfa;
  border-color: var(--color-primary, #0d9488);
  transform: translateY(-2px);
}
.quiz-option-btn:disabled { cursor: default; }
.option-correct {
  background: #c8e6c9 !important;
  border-color: #388e3c !important;
  color: #1b5e20;
}
.option-wrong {
  background: #ffcdd2 !important;
  border-color: #c62828 !important;
  color: #b71c1c;
}

/* Feedback */
.quiz-feedback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-radius: var(--radius-md, 12px);
  font-size: 1rem;
  max-width: 440px;
  width: 100%;
  margin: 0 auto;
}
.feedback-correct {
  background: #e8f5e9;
  border: 2px solid #66bb6a;
  color: #2e7d32;
}
.feedback-wrong {
  background: #ffebee;
  border: 2px solid #ef9a9a;
  color: #c62828;
}
.feedback-swatch {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(0,0,0,0.15);
  flex-shrink: 0;
}
.feedback-text { font-size: 1.05rem; }
.quiz-next-btn { margin-top: 0.2rem; }

/* Results */
.quiz-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
}
.quiz-trophy { font-size: 3.5rem; line-height: 1; }
.quiz-results-heading {
  font-family: var(--font-brand, "Fredoka", sans-serif);
  font-size: 1.75rem;
  margin: 0;
}
.quiz-final-score { font-size: 1.25rem; margin: 0; }
.quiz-result-msg {
  font-size: 1rem;
  color: var(--color-text-muted, #64748b);
  font-style: italic;
  margin: 0;
}

.quiz-result-breakdown {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.6rem;
  margin: 0.5rem 0;
  max-width: 680px;
}
.quiz-history-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.85rem;
  border-radius: var(--radius-sm, 8px);
  font-size: 0.9rem;
  font-weight: 700;
}
.history-correct { background: #e8f5e9; border: 1px solid #a5d6a7; }
.history-wrong   { background: #ffebee; border: 1px solid #ef9a9a; }
.history-swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(0,0,0,0.18);
  flex-shrink: 0;
}
.history-name { min-width: 60px; }
.history-icon { font-size: 0.95rem; }

/* Fade transition */
.fade-enter-active,
.fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ── Responsive ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  .colours-page { padding: 0.75rem 0.85rem 2rem; }

  .page-main-title { font-size: 1.75rem; }

  .section-card { padding: 1.25rem 1rem 1rem; }

  .colour-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.6rem;
  }

  .colour-card { min-height: 100px; }

  .swatch-grid {
    grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  }
  .swatch { height: 58px; }

  .quiz-swatch { width: 140px; height: 140px; }

  .quiz-option-btn {
    min-width: 110px;
    padding: 0.65rem 1rem;
    font-size: 0.92rem;
  }

  .quiz-group-preview {
    grid-template-columns: 1fr 1fr;
  }

  .preview-row { flex-direction: column; gap: 1rem; }
  .triangle-wrap { width: 160px; height: 130px; }
  .triangle-up {
    border-left: 72px solid transparent;
    border-right: 72px solid transparent;
    border-bottom: 124px solid transparent;
  }

  .inspector-swatch { width: 100px; height: 100px; }
  .inspector-top { gap: 1rem; }
  .inspector-name { font-size: 1.4rem; }
  .scale-swatch { width: 36px; height: 28px; }
  .scale-swatch--base { width: 48px; height: 36px; }

  .group-filter {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
  }
  .group-chip { flex-shrink: 0; }
}

@media (max-width: 480px) {
  .colour-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
  .quiz-group-preview {
    grid-template-columns: 1fr;
  }
  .explorer-controls { flex-direction: column; align-items: stretch; }
  .search-wrap { max-width: 100%; }
}
</style>
